import React, { Component } from 'react';
import { Button, ControlLabel, FormControl, FormGroup, Modal, Radio } from 'react-bootstrap';
import { connect } from 'react-redux';

import { KeyLicenseTypeNames, KeyLicenseTypes, OnlineLicenseTypes } from '../common/keyLicenseTypes';
import { isLicenseKeyValid } from '../common/keyValidation';
import OverlayLoader from '../components/overlayLoader';
import * as permanentLicensesActions from '../store/permanentLicensesSlice';
import { permanentLicensesSelectors } from '../store/permanentLicensesSlice';
import * as temporaryLicensesActions from '../store/temporaryLicensesSlice';
import { temporaryLicensesSelectors } from '../store/temporaryLicensesSlice';
import { showModalMessage } from '../store/modalsSlice';

import LicenseKeyInputNormalized from '../components/licenseKeyInputNormalized';
import { unwrapResult } from '@reduxjs/toolkit';

class AssignLicenseToClientDialog extends Component {
  initState = {
    licenseKey: '',
    temporalType: KeyLicenseTypes.OnlinePermanent,
    loadedLicense: null,
  };

  constructor(props) {
    super(props);
    this.handleClose = this.props.onClose;

    this.state = this.initState;

    this.onClose = this.onClose.bind(this);
    this.onKeyChanged = this.onKeyChanged.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onTemporalTypeChanged = this.onTemporalTypeChanged.bind(this);
  }

  onClose() {
    this.handleClose();
    this.setState(this.initState);
  }

  onKeyChanged(normalizedLicenseKey) {
    this.setState({ licenseKey: normalizedLicenseKey }, this.loadLicenseInfo);
  }

  loadLicenseInfo() {
    if (this.isCorrectLicenseKey()) {
      this.props.getLicenseByKey[this.state.temporalType]({ licenseKey: this.state.licenseKey.trim() })
        .then(unwrapResult)
        .then((license) => {
          this.setState({ loadedLicense: license });
        })
    }
  }

  isLodaing() {
    return this.props.isLicenseLoading[this.state.temporalType];
  }

  getLoaderMessage() {
    if (this.props.isLicenseLoading[this.state.temporalType]) {
      return "License loading...";
    }
  }

  isSubmitButtonDisabled() {
    return this.isLodaing() ||
      !this.isCorrectLicenseKey() ||
      this.state.loadedLicense === null ||
      (this.state.loadedLicense.client !== null &&
        this.state.loadedLicense.client.id === this.props.clientId);
  }

  isCorrectLicenseKey() {
    return isLicenseKeyValid(this.state.licenseKey.trim());
  }

  onSubmit() {
    this.onClose();
    this.props.assignLicenseToClient[this.state.temporalType]({
        licenseId: this.state.loadedLicense.id, clientId: this.props.clientId
      }
    )
      .then(unwrapResult)
      .catch(() => {
        const error = this.props.assignmentError[this.state.temporalType];
        this.props.showModalMessage("Error", error && error.toString())
      });
  }

  onTemporalTypeChanged(e) {
    this.setState({ temporalType: e.target.value }, this.loadLicenseInfo);
  }

  renderStatic() {
    return [
      <FormGroup controlId="fcClientName" key="fcClientNameAssign">
        <ControlLabel>Client name</ControlLabel>
        <FormControl.Static>{this.props.clientName}</FormControl.Static>
      </FormGroup>,
      <FormGroup controlId="fcTemporalType" key="fcTemporalTypeAssign">
        <ControlLabel>Temporal type</ControlLabel>
        <div> {
          OnlineLicenseTypes.map(typeKey => {
            return (
              <Radio
                onChange={this.onTemporalTypeChanged}
                checked={this.state.temporalType === typeKey}
                value={typeKey}
                key={typeKey}
              >
                {KeyLicenseTypeNames[typeKey]}
              </Radio>
            )
          })
        } </div>
      </FormGroup>,
      <FormGroup controlId="fcLicenseKey" key="fcLicenseKeyAssign">
        <ControlLabel>License key</ControlLabel>
        <LicenseKeyInputNormalized onChange={this.onKeyChanged} />
      </FormGroup>
    ];
  }

  renderLicenseKeyError() {
    let message = this.state.licenseKey.length === 0 ? {
      text: "Enter license key",
      className: "text-info"
    } : {
      text: "License key is not correct",
      className: "text-danger"
    };
    return <FormControl.Static className={message.className}>{message.text}</FormControl.Static>;
  }

  renderDynamic() {
    return !this.isCorrectLicenseKey() ?
      this.renderLicenseKeyError() :
      this.renderLoadingResults();
  }

  renderLoadingResults() {
    if (this.state.loadedLicense === null) {
      if (this.props.isLicenseLoading[this.state.temporalType])
        return null;
      return <FormControl.Static className="text-danger">License is not found</FormControl.Static>;
    }
    let client = this.state.loadedLicense.client;
    if (client !== null && client.id === this.props.clientId)
      return (
        <FormControl.Static className="text-danger">
          License is already assigned to this client
        </FormControl.Static>
      );
    return [
      client === null || client.id === null ? null :
        <FormControl.Static className="text-warning">
          License is already assigned to client named <strong>{client.fullName}</strong>
        </FormControl.Static>,
      this.renderLicenseInfo()
    ];
  }

  renderLicenseInfo() {
    switch (this.state.temporalType) {
      case KeyLicenseTypes.OnlinePermanent:
        return this.renderPermanentLicenseInfo();
      case KeyLicenseTypes.OnlineTemporary:
        return this.renderTemporaryLicenseInfo();
      default:
        return null;
    }
  }

  renderPermanentLicenseInfo() {
    let lic = this.state.loadedLicense;
    return [
      <FormGroup controlId="fcProductName">
        <ControlLabel>Product name</ControlLabel>
        <FormControl.Static>{lic.productName}</FormControl.Static>
      </FormGroup>,
      <FormGroup controlId="fcGenDate">
        <ControlLabel>Generation date and time</ControlLabel>
        <FormControl.Static>{lic.generationDateTime}</FormControl.Static>
      </FormGroup>,
      <FormGroup controlId="fcLivingState">
        <ControlLabel>Living state</ControlLabel>
        <FormControl.Static>{lic.livingState}</FormControl.Static>
      </FormGroup>
    ];
  }

  renderTemporaryLicenseInfo() {
    let lic = this.state.loadedLicense;
    return [
      <FormGroup controlId="fcProductName">
        <ControlLabel>Product name</ControlLabel>
        <FormControl.Static>{lic.productName}</FormControl.Static>
      </FormGroup>,
      <FormGroup controlId="fcGenDate">
        <ControlLabel>Generation date and time</ControlLabel>
        <FormControl.Static>{lic.generationDateTime}</FormControl.Static>
      </FormGroup>,
      <FormGroup controlId="fcExpDate">
        <ControlLabel>Expiration date and time</ControlLabel>
        <FormControl.Static>{lic.expirationDateTime}</FormControl.Static>
      </FormGroup>
    ];
  }

  render() {
    return (
      <Modal bsSize="small" show={this.props.show} onHide={this.onClose} backdrop="static">
        <Modal.Header>
          <Modal.Title>Assign license to client</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <OverlayLoader active={this.isLodaing()} text={this.getLoaderMessage()}>
            {this.renderStatic()}
            {this.renderDynamic()}
          </OverlayLoader>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={this.onSubmit} disabled={this.isSubmitButtonDisabled()}>
            Assign
          </Button>
          <Button onClick={this.onClose}>Close</Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

const mapStateToProps = (state) => ({
  isLicenseLoading: {
    [KeyLicenseTypes.OnlinePermanent]: permanentLicensesSelectors.isFetching(state),
    [KeyLicenseTypes.OnlineTemporary]: temporaryLicensesSelectors.isFetching(state),
  },
  licenseLoadingError: {
    [KeyLicenseTypes.OnlinePermanent]: permanentLicensesSelectors.fetchingError(state),
    [KeyLicenseTypes.OnlineTemporary]: temporaryLicensesSelectors.fetchingError(state)
  },
  assignmentError: {
    [KeyLicenseTypes.OnlinePermanent]: permanentLicensesSelectors.assignmentError(state),
    [KeyLicenseTypes.OnlineTemporary]: temporaryLicensesSelectors.assignmentError(state),
  },
});

const mapDispatchToProps = (dispatch) => ({
  getLicenseByKey: {
    [KeyLicenseTypes.OnlinePermanent]: (args) =>
      dispatch(permanentLicensesActions.fetchByKey(args)),
    [KeyLicenseTypes.OnlineTemporary]: (args) =>
      dispatch(temporaryLicensesActions.fetchByKey(args)),
  },
  assignLicenseToClient: {
    [KeyLicenseTypes.OnlinePermanent]: (args) =>
      dispatch(permanentLicensesActions.assignToClient(args)),
    [KeyLicenseTypes.OnlineTemporary]: (args) =>
      dispatch(temporaryLicensesActions.assignToClient(args)),
  },
  showModalMessage: (title, text) => dispatch(showModalMessage({ title, text })),
});

export default connect(mapStateToProps, mapDispatchToProps)(AssignLicenseToClientDialog);



// WEBPACK FOOTER //
// ./src/containers/assignLicenseToClientDialog.js